import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  forwardRef,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { MheInputChangeEvent } from './models/input-change-event.model';

export interface MheInputFocusEvent {
  event: FocusEvent
  componentInstance: InputComponent
}

/**
 * Reader-owned replacement for ngx-shared's deprecated `mhe-input`.
 *
 * Markup, CSS classes and ARIA wiring intentionally match ngx-shared so the global
 * Open Learning theme (`mhe-input .form-group > i`, `input.form-control.has-icon`)
 * and Reader overrides (`.rdrx-hide-label`) keep styling every usage identically.
 *
 * Text-only by design: Reader's only consumers (Search Classic, TOC search) take free text, so
 * ngx-shared's `type` input (email/number/password) was intentionally not carried over.
 */
@Component({
  // Retain the shared selector so existing templates and the element-scoped theme rules keep working.
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'mhe-input',
  templateUrl: './input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor {
  readonly disabled = input<boolean | null>(null);
  readonly error = input(false);
  readonly feedback = input<string[]>([]);
  /** Open Learning icon name without the `dpg-icon-` prefix, e.g. `search`. */
  readonly icon = input('');
  /**
   * Applied to the native input so the visible/hidden `<label for>` names it. Bind it (`[id]="'x'"`)
   * rather than using a static `id="x"` attribute, which would also duplicate the id on the host element.
   */
  readonly id = input<string | null>(null);
  readonly label = input<string | null>(null);
  readonly maxlength = input<number | null>(null);
  readonly placeholder = input<string | null>(null);
  readonly required = input(false);
  readonly success = input(false);
  readonly value = input<string | null>(null);

  readonly inputChange = output<MheInputChangeEvent>();
  readonly inputFocus = output<MheInputFocusEvent>();
  readonly inputBlur = output<MheInputChangeEvent>();

  // Decorator query with `static: true` so hosts can reach the native input during their ngOnInit
  // (SearchComponent sets aria-label/autocomplete there), matching the ngx-shared contract.
  @ViewChild('input', { static: true }) readonly inputEl!: ElementRef<HTMLInputElement>;

  // ngx-shared only stored values written from outside, so after typing, a forms reset back to the
  // previous value (e.g. '' -> 'abc' typed -> '') was ignored and the field never cleared.
  // Tracking typed text here keeps writeValue authoritative.
  protected readonly currentValue = linkedSignal(() => this.value() ?? '');
  private readonly formsDisabled = signal(false);

  protected readonly isDisabled = computed(() => !!this.disabled() || this.formsDisabled());
  protected readonly hasFeedback = computed(() => this.success() || this.error());
  protected readonly helpBlockId = computed(() => `help-block-${this.id()}`);
  protected readonly describedBy = computed(() => (this.hasFeedback() ? this.helpBlockId() : null));
  protected readonly ariaInvalid = computed(() => {
    if (!this.hasFeedback()) {
      return null;
    }
    return this.success() ? 'false' : 'true';
  });

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  protected onInput(event: Event, value: string): void {
    this.currentValue.set(value);
    this.inputChange.emit({ event, value });
    this.onChange(value);
  }

  protected onFocus(event: FocusEvent): void {
    this.inputFocus.emit({ event, componentInstance: this });
  }

  protected onBlur(event: Event, value: string): void {
    this.onTouched();
    this.inputBlur.emit({ event, value });
  }

  writeValue(value: string | null | undefined): void {
    this.currentValue.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formsDisabled.set(isDisabled);
  }
}
